import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/organisms/Footer';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';
import SessionManager from '../utils/sessionManager';
import BookDemoModal from '../components/organisms/BookDemoModal';

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, suffix = '', prefix = '' }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const counterRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => {
      if (counterRef.current) {
        observer.unobserve(counterRef.current);
      }
    };
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    let animationFrame;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = (currentTime - startTime) / duration;

      if (progress < 1) {
        setCount(Math.floor(end * progress));
        animationFrame = requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [isVisible, end, duration]);

  return (
    <span ref={counterRef}>
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  );
};

const PlacementTrainingLandingPage = () => {
  const navigate = useNavigate();
  const [currentSession, setCurrentSession] = useState(null);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [showBookDemoModal, setShowBookDemoModal] = useState(false);

  useEffect(() => {
    document.title = 'Aptirix Placement Training - Prepare Students for Success';
    
    const session = SessionManager.getCurrentSession();
    setCurrentSession(session);

    // Show Book Demo popup once per session
    const hasSeenDemoPopup = sessionStorage.getItem('hasSeenPlacementDemoPopup');
    if (!hasSeenDemoPopup) {
      // Show popup after 3 seconds
      const timer = setTimeout(() => {
        setShowBookDemoModal(true);
        sessionStorage.setItem('hasSeenPlacementDemoPopup', 'true');
      }, 3000);
      
      return () => clearTimeout(timer);
    }

    const handleClickOutside = (event) => {
      if (!event.target.closest('.profile-dropdown-container')) {
        setProfileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleBookDemo = () => {
    setShowBookDemoModal(true);
  };

  const handleSmoothScroll = (e, targetId) => {
    e.preventDefault();
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const isCandidate = currentSession?.role === 'candidate';
  const isHR = currentSession?.role === 'hr';

  return (
    <div className="min-h-screen bg-white">
      {/* Book Demo Modal */}
      <BookDemoModal 
        isOpen={showBookDemoModal} 
        onClose={() => setShowBookDemoModal(false)} 
      />

      {/* Navigation */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <span className="font-bold text-2xl sm:text-3xl text-gray-900">
              Aptirix
            </span>
            <span className="ml-2 text-sm text-gray-600 hidden sm:inline">Placement Training</span>
          </div>

          {/* Navigation Links & Auth */}
          <div className="flex items-center gap-6">
            {/* Navigation Links */}
            <div className="hidden md:flex items-center gap-6">
              <a 
                href="#features" 
                onClick={(e) => handleSmoothScroll(e, 'features')}
                className="text-gray-700 hover:text-[#2b6cee] font-medium transition-colors cursor-pointer"
              >
                Features
              </a>
              <a 
                href="#benefits" 
                onClick={(e) => handleSmoothScroll(e, 'benefits')}
                className="text-gray-700 hover:text-[#2b6cee] font-medium transition-colors cursor-pointer"
              >
                Benefits
              </a>
              <a 
                href="#how-it-works" 
                onClick={(e) => handleSmoothScroll(e, 'how-it-works')}
                className="text-gray-700 hover:text-[#2b6cee] font-medium transition-colors cursor-pointer"
              >
                How It Works
              </a>
            </div>

            {/* Auth Section */}
            {currentSession ? (
              <div className="relative profile-dropdown-container">
                <button
                  onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                  className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 sm:px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#2b6cee] to-[#1a4fb8] flex items-center justify-center">
                    <span className="text-white text-sm font-semibold">
                      {(currentSession.email?.charAt(0) || 'U').toUpperCase()}
                    </span>
                  </div>
                  <span className="max-w-[120px] truncate hidden sm:inline">
                    {currentSession.email?.split('@')[0] || 'User'}
                  </span>
                  <Icon name="expand_more" size={20} />
                </button>
                
                {profileMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 rounded-lg border border-gray-200 bg-white p-2 shadow-xl">
                    <div className="px-3 py-2 border-b border-gray-100">
                      <p className="text-xs text-gray-500 truncate">{currentSession.email}</p>
                    </div>
                    <button
                      onClick={() => navigate(isCandidate ? '/candidate/dashboard' : isHR ? '/hr/dashboard' : '/')}
                      className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-gray-700 hover:bg-gray-50 mt-1"
                    >
                      <Icon name="dashboard" size={16} />
                      Dashboard
                    </button>
                    <button
                      onClick={() => {
                        SessionManager.clearAllSessions();
                        setCurrentSession(null);
                        setProfileMenuOpen(false);
                        navigate('/');
                        window.location.reload();
                      }}
                      className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-red-600 hover:bg-red-50 mt-1"
                    >
                      <Icon name="logout" size={16} />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate('/login')}
                  className="text-gray-700 hover:text-[#2b6cee] font-medium transition-colors"
                >
                  Sign In
                </button>
                <Button
                  onClick={() => navigate('/signup')}
                  className="bg-[#2b6cee] hover:bg-[#1a4fb8] text-white"
                >
                  Get Started
                </Button>
              </div>
            )}
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] text-white overflow-hidden min-h-[700px]">
        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/4 w-[900px] h-[900px] rounded-full bg-gradient-to-br from-cyan-400/30 via-blue-500/35 to-indigo-600/30 blur-3xl"></div>
          <div className="absolute left-0 top-0 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-purple-500/35 via-violet-500/30 to-fuchsia-500/25 blur-3xl"></div>
          <div className="absolute right-0 top-10 w-[450px] h-[450px] rounded-full bg-gradient-to-bl from-cyan-400/30 via-sky-500/25 to-blue-600/30 blur-3xl"></div>
          <div className="absolute left-10 bottom-0 w-[550px] h-[550px] rounded-full bg-gradient-to-tr from-blue-600/30 via-cyan-500/25 to-teal-400/20 blur-3xl"></div>
          <div className="absolute right-0 bottom-0 w-[480px] h-[480px] rounded-full bg-gradient-to-tl from-pink-500/25 via-rose-500/20 to-orange-400/15 blur-3xl"></div>
        </div>
        
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxkZWZzPjxwYXR0ZXJuIGlkPSJncmlkIiB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiPjxwYXRoIGQ9Ik0gNDAgMCBMIDAgMCAwIDQwIiBmaWxsPSJub25lIiBzdHJva2U9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-40"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-32">
          <div className="text-center max-w-4xl mx-auto flex flex-col items-center">
            <div className="inline-block mb-6 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium border border-white/20">
              AI-POWERED PLACEMENT TRAINING FOR COLLEGES
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="block">Prepare Your Students for</span>
              <span className="block">Successful Placements</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-200 mb-8 max-w-3xl mx-auto">
              Empower your students with AI-driven interview training, skill assessments, and personalized feedback 
              to boost placement success rates and build industry-ready talent.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center w-full">
              <Button 
                size="xl" 
                onClick={handleBookDemo}
                className="bg-white text-gray-900 hover:bg-gray-100 px-10 py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all inline-flex items-center justify-center gap-2"
              >
                <Icon name="calendar_today" size={20} />
                Book a Demo
              </Button>
              <Button 
                size="xl" 
                variant="outline"
                onClick={() => navigate('/signup')}
                className="border-2 border-white text-white hover:bg-white/10 px-10 py-4 text-lg font-semibold rounded-xl inline-flex items-center justify-center gap-2"
              >
                <Icon name="school" size={20} />
                Get Started
              </Button>
            </div>
          </div>

          {/* Feature Tags */}
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: 'psychology', title: 'AI Interview Training', description: 'Real-time practice sessions' },
              { icon: 'assessment', title: 'Skill Assessment', description: 'Comprehensive evaluations' },
              { icon: 'analytics', title: 'Performance Analytics', description: 'Detailed insights' },
              { icon: 'workspace_premium', title: 'Placement Ready', description: 'Industry-standard prep' },
            ].map((feature, index) => (
              <div 
                key={index}
                className="bg-white/10 backdrop-blur-md rounded-2xl px-6 py-4 flex items-center space-x-3 border border-white/20 hover:bg-white/15 transition-all"
              >
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm flex-shrink-0">
                  <Icon name={feature.icon} size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">{feature.title}</h3>
                  <p className="text-sm text-gray-200">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Showcase Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left - Image */}
            <div className="order-2 lg:order-1">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-br from-blue-50 to-indigo-100">
                <img 
                  src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&q=80" 
                  alt="Students in Placement Training"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<div class="flex items-center justify-center h-96"><div class="text-center"><svg class="w-24 h-24 mx-auto text-blue-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path></svg><p class="text-gray-600 text-lg font-semibold">Placement Training Platform</p></div></div>';
                  }}
                />
              </div>
            </div>

            {/* Right - Content */}
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Transform Your College Placement Program
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Equip your students with cutting-edge AI technology to practice interviews, receive instant feedback, 
                and develop the skills needed to excel in campus placements and beyond.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Icon name="check_circle" size={24} className="text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Unlimited Practice Sessions</h4>
                    <p className="text-gray-600">Students can practice anytime, anywhere with AI-powered mock interviews</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="check_circle" size={24} className="text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Instant Performance Feedback</h4>
                    <p className="text-gray-600">Real-time analysis of communication skills, technical knowledge, and confidence</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="check_circle" size={24} className="text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Comprehensive Reports</h4>
                    <p className="text-gray-600">Detailed PDF reports for students and placement officers to track progress</p>
                  </div>
                </div>
              </div>
              <Button 
                size="lg" 
                onClick={handleBookDemo}
                className="mt-8 bg-[#2b6cee] hover:bg-[#1a4fb8] text-white"
              >
                Schedule a Demo
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Aptirix for Placement Training Section */}
      <section id="features" className="py-20 bg-blue-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-16">
            Why Aptirix for Placement Training?
          </h2>

          {/* Feature Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {/* Card 1 - Company-Specific Training */}
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Trending Company Questions</h3>
              <p className="text-gray-600 text-sm mb-6">
                Practice with real interview questions from top companies like Google, Microsoft, Amazon, and more. Stay updated with latest hiring trends.
              </p>
              <button className="text-sm font-medium text-gray-900 border border-gray-900 px-4 py-2 rounded-lg hover:bg-gray-900 hover:text-white transition-colors">
                Learn More
              </button>
            </div>

            {/* Card 2 - Round-Based Training */}
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Round-Based Preparation</h3>
              <p className="text-gray-600 text-sm mb-6">
                Structured training for aptitude, technical, HR, and group discussion rounds. Complete preparation for every stage of placement process.
              </p>
              <button className="text-sm font-medium text-gray-900 border border-gray-900 px-4 py-2 rounded-lg hover:bg-gray-900 hover:text-white transition-colors">
                Learn More
              </button>
            </div>

            {/* Card 3 - Customized Training */}
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Customized Training</h3>
              <p className="text-gray-600 text-sm mb-6">
                Personalized training plans based on student's branch, skills, and target companies. AI adapts difficulty based on performance.
              </p>
              <button className="text-sm font-medium text-gray-900 border border-gray-900 px-4 py-2 rounded-lg hover:bg-gray-900 hover:text-white transition-colors">
                Learn More
              </button>
            </div>

            {/* Card 4 - Performance Analytics */}
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Real-Time Analytics</h3>
              <p className="text-gray-600 text-sm mb-6">
                Track student progress with detailed dashboards. Identify weak areas and get personalized recommendations for improvement.
              </p>
              <button className="text-sm font-medium text-gray-900 border border-gray-900 px-4 py-2 rounded-lg hover:bg-gray-900 hover:text-white transition-colors">
                Learn More
              </button>
            </div>
          </div>

          {/* Statistics Section */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 pt-12 border-t border-gray-200">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">
                <AnimatedCounter end={10} suffix="+" />
              </div>
              <div className="text-sm text-gray-600">Partner Colleges</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">
                <AnimatedCounter end={5} suffix="K+" />
              </div>
              <div className="text-sm text-gray-600">Students Trained</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">
                <AnimatedCounter end={92} suffix="%" />
              </div>
              <div className="text-sm text-gray-600">Success Rate</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">
                <AnimatedCounter end={35} suffix="%" />
              </div>
              <div className="text-sm text-gray-600">Placement Increase</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">24/7</div>
              <div className="text-sm text-gray-600">AI Availability</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">
                <AnimatedCounter end={100} suffix="+" />
              </div>
              <div className="text-sm text-gray-600">Companies Covered</div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Training Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything your students need to succeed in campus placements
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200 group">
              <div className="w-16 h-16 bg-gradient-to-br from-[#2b6cee] to-[#1a4fb8] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Icon name="psychology" size={32} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">AI Mock Interviews</h3>
              <p className="text-gray-600 leading-relaxed">
                Realistic interview simulations with AI that adapts to student responses, covering technical, 
                HR, and behavioral questions tailored to different industries.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200 group">
              <div className="w-16 h-16 bg-gradient-to-br from-[#2b6cee] to-[#1a4fb8] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Icon name="assessment" size={32} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Skill Assessments</h3>
              <p className="text-gray-600 leading-relaxed">
                Comprehensive evaluations of technical skills, aptitude, communication abilities, and 
                problem-solving capabilities with detailed scoring and benchmarking.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200 group">
              <div className="w-16 h-16 bg-gradient-to-br from-[#2b6cee] to-[#1a4fb8] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Icon name="business" size={32} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Company-Specific Prep</h3>
              <p className="text-gray-600 leading-relaxed">
                Targeted preparation for specific companies with their actual interview patterns, 
                question types, and evaluation criteria from past placement drives.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200 group">
              <div className="w-16 h-16 bg-gradient-to-br from-[#2b6cee] to-[#1a4fb8] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Icon name="description" size={32} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Resume Builder</h3>
              <p className="text-gray-600 leading-relaxed">
                AI-powered resume creation and optimization tools that help students craft ATS-friendly 
                resumes highlighting their skills and achievements effectively.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200 group">
              <div className="w-16 h-16 bg-gradient-to-br from-[#2b6cee] to-[#1a4fb8] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Icon name="groups" size={32} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Group Discussion Training</h3>
              <p className="text-gray-600 leading-relaxed">
                Practice group discussions with AI-simulated participants. Get feedback on communication, 
                leadership, and teamwork skills essential for GD rounds.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200 group">
              <div className="w-16 h-16 bg-gradient-to-br from-[#2b6cee] to-[#1a4fb8] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Icon name="verified" size={32} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Skill Verification</h3>
              <p className="text-gray-600 leading-relaxed">
                AI-verified skill certificates that students can showcase to recruiters, providing 
                credible proof of their competencies and interview readiness.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Benefits for Your College
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Proven results that transform placement outcomes
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Benefit 1 */}
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Icon name="trending_up" size={28} className="text-blue-600" />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Increase Placement Rates</h3>
                <p className="text-gray-600 leading-relaxed">
                  Colleges using Aptirix report up to 35% improvement in placement success rates. 
                  Better-prepared students perform confidently in real interviews and secure more offers.
                </p>
              </div>
            </div>

            {/* Benefit 2 */}
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center">
                  <Icon name="savings" size={28} className="text-green-600" />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Cost-Effective Solution</h3>
                <p className="text-gray-600 leading-relaxed">
                  Reduce expenses on external training programs and placement consultants. 
                  One platform provides unlimited training for all students at a fraction of traditional costs.
                </p>
              </div>
            </div>

            {/* Benefit 3 */}
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Icon name="schedule" size={28} className="text-purple-600" />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">24/7 Availability</h3>
                <p className="text-gray-600 leading-relaxed">
                  Students can practice anytime, anywhere without scheduling constraints. 
                  No need to coordinate with trainers or wait for mock interview sessions.
                </p>
              </div>
            </div>

            {/* Benefit 4 */}
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center">
                  <Icon name="insights" size={28} className="text-orange-600" />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Data-Driven Insights</h3>
                <p className="text-gray-600 leading-relaxed">
                  Placement officers get comprehensive dashboards showing student readiness, 
                  skill gaps, and progress metrics to make informed decisions and provide targeted support.
                </p>
              </div>
            </div>

            {/* Benefit 5 */}
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center">
                  <Icon name="workspace_premium" size={28} className="text-red-600" />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Industry-Standard Training</h3>
                <p className="text-gray-600 leading-relaxed">
                  Our AI is trained on thousands of real interviews from top companies. 
                  Students experience authentic interview scenarios and industry-relevant questions.
                </p>
              </div>
            </div>

            {/* Benefit 6 */}
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center">
                  <Icon name="support_agent" size={28} className="text-teal-600" />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Dedicated Support</h3>
                <p className="text-gray-600 leading-relaxed">
                  Our team provides onboarding, training for placement officers, and ongoing support 
                  to ensure successful implementation and maximum student engagement.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Simple implementation process for your college
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative">
            {/* Connecting lines for desktop */}
            <div className="hidden md:block absolute top-12 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#2b6cee]/30 to-transparent"></div>
            
            {/* Step 1 */}
            <div className="text-center relative">
              <div className="relative mb-8 inline-block">
                <div className="w-24 h-24 bg-gradient-to-br from-[#2b6cee] to-[#1a4fb8] rounded-3xl flex items-center justify-center mx-auto shadow-xl hover:scale-110 transition-transform duration-300 relative z-10">
                  <span className="text-4xl font-bold text-white">1</span>
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Sign Up</h3>
              <p className="text-gray-600 leading-relaxed">
                Register your college and get institutional access for all students and placement officers.
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center relative">
              <div className="relative mb-8 inline-block">
                <div className="w-24 h-24 bg-gradient-to-br from-[#2b6cee] to-[#1a4fb8] rounded-3xl flex items-center justify-center mx-auto shadow-xl hover:scale-110 transition-transform duration-300 relative z-10">
                  <span className="text-4xl font-bold text-white">2</span>
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Onboard Students</h3>
              <p className="text-gray-600 leading-relaxed">
                Students create profiles and complete initial assessments to establish baseline skills.
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center relative">
              <div className="relative mb-8 inline-block">
                <div className="w-24 h-24 bg-gradient-to-br from-[#2b6cee] to-[#1a4fb8] rounded-3xl flex items-center justify-center mx-auto shadow-xl hover:scale-110 transition-transform duration-300 relative z-10">
                  <span className="text-4xl font-bold text-white">3</span>
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Practice & Learn</h3>
              <p className="text-gray-600 leading-relaxed">
                Students engage in unlimited AI mock interviews and receive instant feedback for improvement.
              </p>
            </div>

            {/* Step 4 */}
            <div className="text-center relative">
              <div className="relative mb-8 inline-block">
                <div className="w-24 h-24 bg-gradient-to-br from-[#2b6cee] to-[#1a4fb8] rounded-3xl flex items-center justify-center mx-auto shadow-xl hover:scale-110 transition-transform duration-300 relative z-10">
                  <span className="text-4xl font-bold text-white">4</span>
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Track Progress</h3>
              <p className="text-gray-600 leading-relaxed">
                Monitor student readiness through analytics dashboards and prepare them for campus placements.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              What Placement Officers Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real feedback from colleges using Aptirix
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#2b6cee] to-[#1a4fb8] rounded-full flex items-center justify-center text-white font-bold text-lg">
                  R
                </div>
                <div className="ml-4">
                  <h4 className="font-bold text-gray-900">Dr. Rajesh Kumar</h4>
                  <p className="text-sm text-gray-600">Placement Officer, Engineering College</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                "Aptirix has revolutionized our placement training. Students are more confident, 
                and our placement rates have increased by 35% in just one year. The AI feedback is incredibly detailed."
              </p>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#2b6cee] to-[#1a4fb8] rounded-full flex items-center justify-center text-white font-bold text-lg">
                  P
                </div>
                <div className="ml-4">
                  <h4 className="font-bold text-gray-900">Prof. Priya Sharma</h4>
                  <p className="text-sm text-gray-600">Head of Placements, Business School</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                "The analytics dashboard gives us clear visibility into student readiness. 
                We can now provide targeted support where needed. It's a game-changer for placement preparation."
              </p>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#2b6cee] to-[#1a4fb8] rounded-full flex items-center justify-center text-white font-bold text-lg">
                  A
                </div>
                <div className="ml-4">
                  <h4 className="font-bold text-gray-900">Dr. Anil Verma</h4>
                  <p className="text-sm text-gray-600">Dean, Technical Institute</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                "Cost-effective and scalable solution. All our students get equal access to quality training. 
                The ROI is exceptional compared to traditional training programs."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Ready to Transform Your Placement Program?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Join leading colleges in preparing students for successful careers. 
            Schedule a demo to see how Aptirix can boost your placement rates.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="xl" 
              onClick={handleBookDemo}
              className="bg-white text-gray-900 hover:bg-gray-100 px-10 py-4 text-lg font-semibold rounded-xl inline-flex items-center justify-center gap-2"
            >
              <Icon name="calendar_today" size={20} />
              Book a Demo
            </Button>
            <Button 
              size="xl" 
              onClick={() => navigate('/signup')}
              className="bg-[#2b6cee] hover:bg-[#1a4fb8] text-white px-10 py-4 text-lg font-semibold rounded-xl inline-flex items-center justify-center gap-2"
            >
              <Icon name="arrow_forward" size={20} />
              Get Started
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PlacementTrainingLandingPage;
